import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Field, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasContextPreview, type CanvasContextPreviewBody, type PreviewProfile } from '../../../../api/client.ts';
import { DEFAULT_PREVIEW_SEED, ProfileSelector } from '../../../composer/shared/ProfileSelector.tsx';
import { parseJsonObject } from '../../../composer/shared/personalization.ts';
import type { ContextConfig, ContextVariableType } from './contextConfig.ts';
import { contextValueText, exampleContext, exampleEvent, exampleText, notUpdatedReason } from './contextPreviewData.ts';

export interface ContextPreviewProps {
  environmentId: string;
  config: ContextConfig;
  /** The settings pass the contract; the server is asked only then. */
  valid: boolean;
  /** What earlier Context steps set: the example context before the step. */
  earlier: ReadonlyArray<{ name: string; types: readonly ContextVariableType[] }>;
  /** The employee holds `canvas.edit`, which the preview needs. */
  canPreview: boolean;
}

function errorText(error: unknown, profile: PreviewProfile | null): string {
  if (!(error instanceof ApiError)) return 'The preview could not be worked out.';
  if (error.code === 'profile_not_found') {
    if (profile?.kind === 'external_id') return 'No user has this external ID in the environment.';
    if (profile?.kind === 'profile') return 'This test user is no longer in the environment: choose another one.';
    return 'The environment has no active users to pick from.';
  }
  if (error.status === 403) return 'Previewing needs the canvas.edit permission.';
  if (error.code === 'validation_failed') return 'The server could not read these settings.';
  return error.message;
}

/** Room for the example as it is, between 3 and 10 lines. */
const rowsFor = (text: string) => Math.min(10, Math.max(3, text.split('\n').length));

interface Ran {
  body: CanvasContextPreviewBody;
  config: ContextConfig;
}

/** The context after the step: this step's variables in order, then what the entry carried from before. */
function ResultTable({ ran, result }: { ran: Ran; result: CanvasContextPreview }) {
  const own = new Set(ran.config.variables.map((v) => v.name));
  const before = ran.body.context ?? {};
  const failed = new Map(result.notUpdated.map((n) => [n.name, n.reason]));
  const has = (name: string) => Object.hasOwn(result.context, name);
  return (
    <table className="table context-preview__table" aria-label="Context after this step">
      <thead>
        <tr>
          <th scope="col">Variable</th>
          <th scope="col">Value</th>
          <th scope="col">Result</th>
        </tr>
      </thead>
      <tbody>
        {ran.config.variables.map((v) => {
          const reason = failed.get(v.name);
          return (
            <tr key={v.name}>
              <th scope="row">
                <code>{v.name}</code>
              </th>
              <td>{has(v.name) ? <code>{contextValueText(result.context[v.name])}</code> : <span className="hint">not set</span>}</td>
              <td>
                {reason !== undefined ? (
                  <span className="field-error">
                    Not updated: {notUpdatedReason(reason)}. {Object.hasOwn(before, v.name) ? 'It keeps the value from before.' : 'It stays unset.'}
                  </span>
                ) : result.updated.includes(v.name) ? (
                  'Updated'
                ) : (
                  <span className="hint">Not worked out</span>
                )}
              </td>
            </tr>
          );
        })}
        {Object.keys(result.context)
          .filter((name) => !own.has(name))
          .map((name) => (
            <tr key={name}>
              <th scope="row">
                <code>{name}</code>
              </th>
              <td>
                <code>{contextValueText(result.context[name])}</code>
              </td>
              <td className="hint">From before this step</td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

/**
 * «Preview» of a Context step (`POST …/canvas-steps/context/preview`, BB-119): pick a user the way message previews
 * do, give an example of the event that brought them here and the context they carry, and see the context after the
 * step — which variables updated, which did not and why — worked out by the runtime's handler and Liquid engine.
 */
export function ContextPreview({ environmentId, config, valid, earlier, canPreview }: ContextPreviewProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [profile, setProfile] = useState<PreviewProfile | null>({ kind: 'random', seed: DEFAULT_PREVIEW_SEED });
  const [eventText, setEventText] = useState(() => exampleText(exampleEvent(config)));
  const [contextText, setContextText] = useState(() => exampleText(exampleContext(config, earlier)));
  const event = parseJsonObject(eventText);
  const context = parseJsonObject(contextText);
  const body: CanvasContextPreviewBody | null =
    valid && profile && !event.error && !context.error ? { config, profile, ...(context.value ? { context: context.value } : {}), ...(event.value ? { eventProperties: event.value } : {}) } : null;
  // The last result stays while the next preview is worked out.
  const [shown, setShown] = useState<{ ran: Ran; result: CanvasContextPreview }>();
  const preview = useMutation({
    mutationFn: (ran: Ran) => api.previewCanvasContext(environmentId, ran.body),
    onSuccess: (result, ran) => setShown({ ran, result }),
    onError: () => setShown(undefined),
  });
  const outdated = Boolean(shown && JSON.stringify(shown.ran.body) !== JSON.stringify(body));
  const fill = () => {
    setEventText(exampleText(exampleEvent(config)));
    setContextText(exampleText(exampleContext(config, earlier)));
  };

  return (
    <section className="notice step-panel__summary context-preview" aria-label="Preview">
      <h4>Preview</h4>
      {!canPreview ? (
        <p className="hint">Previewing the step needs the canvas.edit permission.</p>
      ) : (
        <>
          <p className="hint">Works the variables out for one user and an example event, the way the step will when a user reaches it.</p>
          <ProfileSelector environmentId={environmentId} value={profile} onChange={setProfile} />
          <Field label="Example event properties (JSON)" hint="The event that brought the user to this step (event_properties); prefilled with the properties the values read." error={event.error}>
            <Textarea rows={rowsFor(eventText)} spellCheck={false} value={eventText} placeholder='{"amount": 100, "currency": "USD"}' onChange={(e) => setEventText(e.target.value)} />
          </Field>
          <Field label="Context before this step (JSON)" hint="What the entry already carries (context): earlier Context steps and the Canvas entry; prefilled from the earlier steps." error={context.error}>
            <Textarea rows={rowsFor(contextText)} spellCheck={false} value={contextText} placeholder='{"user_currency": "USD"}' onChange={(e) => setContextText(e.target.value)} />
          </Field>
          <div className="toolbar">
            <Button variant="primary" size="sm" loading={preview.isPending} disabled={!body} onClick={() => body && preview.mutate({ body, config })}>
              Preview
            </Button>
            <Button size="sm" variant="ghost" onClick={fill}>
              Fill the examples from the step
            </Button>
            {!valid ? <span className="hint">Fix the settings above to preview.</span> : !profile ? <span className="hint">Enter an external ID or choose a test user.</span> : null}
          </div>
          {preview.isError && <p role="alert">{errorText(preview.error, preview.variables?.body.profile ?? null)}</p>}
          {shown && (
            <div className="context-preview__result" aria-busy={preview.isPending || undefined}>
              <p role="status">
                <strong>
                  {shown.result.updated.length} of {shown.ran.config.variables.length} {shown.ran.config.variables.length === 1 ? 'variable' : 'variables'} updated
                </strong>
                {shown.result.profileId && (
                  <>
                    {' '}
                    for user <code>{shown.result.profileId}</code>
                  </>
                )}
                .{outdated ? ' The settings or the examples changed since: preview again to update it.' : ''}
              </p>
              <ResultTable ran={shown.ran} result={shown.result} />
              {shown.result.masked && <p className="hint">Personal attributes of this user show as ••• (seeing them needs the pii.view permission), so values made from them show ••• too.</p>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
